import { useRef, useState } from "react"
import { NumberControl } from "../../ui"
import { fieldError } from "../../fieldRules"

const InlineEditCell = ({ value, onSave, display, rule, type = "text", width = 120, align = "left" }) => {
  const [draft, setDraft] = useState(null)
  const [error, setError] = useState(null)
  // Escape unmounts the focused input, which can still fire blur → commit.
  const cancelled = useRef(false)

  const start = () => {
    cancelled.current = false
    setError(null)
    setDraft(value == null ? "" : String(value))
  }

  const close = () => {
    setError(null)
    setDraft(null)
  }

  const commit = () => {
    if (cancelled.current || draft === null) return
    const trimmed = draft.trim()
    const next = type === "number" ? Number(trimmed) : trimmed
    const msg = rule && fieldError(trimmed, rule)
    if (msg) {
      setError(msg)
      return
    }
    close()
    // Blank is a no-op on PATCH; skip the call instead of toasting a fake success.
    if (trimmed === "" || Number.isNaN(next) || next === value) return
    onSave(next)
  }

  const onKeyDown = (e) => {
    if (e.key === "Enter") e.currentTarget.blur()
    if (e.key === "Escape") {
      cancelled.current = true
      close()
    }
  }

  const stop = (e) => e.stopPropagation()
  const style = {
    width,
    height: 30,
    textAlign: align,
    borderColor: error ? "var(--pc-danger, #b42318)" : undefined,
  }

  if (draft === null) {
    return (
      <span
        role="button"
        tabIndex={0}
        title="Click to edit"
        style={{ cursor: "text", borderBottom: "1px dashed var(--text-muted)" }}
        onClick={(e) => {
          stop(e)
          start()
        }}
        onKeyDown={(e) => e.key === "Enter" && start()}
      >
        {display ?? (value || "—")}
      </span>
    )
  }

  const input =
    type === "number" ? (
      <NumberControl
        autoFocus
        className="pc-control"
        style={style}
        value={draft}
        onChange={setDraft}
        onClick={stop}
        onBlur={commit}
        onKeyDown={onKeyDown}
      />
    ) : (
      <input
        autoFocus
        className="pc-control"
        style={style}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onClick={stop}
        onBlur={commit}
        onKeyDown={onKeyDown}
      />
    )

  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
      {input}
      {error && (
        <span
          role="alert"
          style={{
            color: "var(--pc-danger, #b42318)",
            fontSize: 11,
            fontWeight: 400,
          }}
        >
          {error}
        </span>
      )}
    </span>
  )
}

export default InlineEditCell
